/**
 * Recognises the IndexedDB failures that WebKit reports when iOS ends a transaction.
 *
 * A device reported this message:
 *   UnknownError: Attempt to delete range from database without an in-progress transaction
 *
 * WebKit's IndexedDB server returns that error when it no longer holds the request's transaction
 * open. Suspending the web view does that. The camera and the photo picker both suspend it.
 *
 * These errors arrive as bare DOMExceptions, not as Errors, and Firestore does not change that. Its
 * SimpleDb rejects a failed request with the DOMException itself, rather than with the
 * IndexedDbTransactionError that it uses for transaction failures. So nothing downstream recognises
 * the error, and that includes the SDK's own recovery. A check for FirebaseError here would miss it
 * as well.
 *
 * The names below are the ones IndexedDB raises. Other APIs raise some of them too, so this function
 * can match a DOMException that IndexedDB did not raise. That costs one Sentry report, and one
 * health check for Firestore. It is the cheaper error to make.
 */
const INDEXED_DB_ERROR_NAMES = [
	'UnknownError',
	'AbortError',
	'InvalidStateError',
	'TransactionInactiveError',
	'ConstraintError',
	'QuotaExceededError',
	'VersionError',
	'TimeoutError',
	'DataError',
	'NotFoundError',
]

export function isIndexedDBError(reason: unknown): boolean {
	if (typeof DOMException === 'undefined' || !(reason instanceof DOMException)) {
		return false
	}

	return INDEXED_DB_ERROR_NAMES.indexOf(reason.name) !== -1
}
